import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, ArrowLeft, Lock, Shield, X, CheckCircle, AlertCircle, Briefcase, Edit2, Trash2, Power, Key } from 'lucide-react';
import api from '../../services/api';

const OrgTree = ({ users }) => {
    if (!users || users.length === 0) return null;

    return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', paddingTop: '20px' }}>
            {users.map((user) => (
                <div key={user.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                        position: 'relative',
                        padding: '12px',
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        zIndex: 10,
                        minWidth: '200px',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <div style={{
                            width: '40px', height: '40px', borderRadius: '50%',
                            backgroundColor: user.role === 'ADMIN' ? '#f3e8ff' : '#dbeafe',
                            color: user.role === 'ADMIN' ? '#6b21a8' : '#1e40af',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold',
                            fontSize: '1.1rem', marginBottom: '8px'
                        }}>
                            {user.name.charAt(0)}
                        </div>
                        <div style={{ fontWeight: '600', color: '#111827', fontSize: '0.95rem' }}>{user.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                            <span style={{
                                textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 'bold',
                                backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px'
                            }}>
                                {user.role}
                            </span>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#4b5563', marginTop: '4px' }}>
                            {user.department || 'General'}
                        </div>
                    </div>

                    {user.children && user.children.length > 0 && (
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                width: '2px', height: '20px', backgroundColor: '#cbd5e1', margin: '0 auto'
                            }}></div>

                            <div style={{
                                display: 'flex', position: 'relative', paddingTop: '20px'
                            }}>
                                <div style={{
                                    position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                                    width: '100%', borderTop: '2px solid #cbd5e1'
                                }}></div>

                                <OrgTree users={user.children} />
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

const UserModal = ({ show, onClose, onSubmit, loading, managers, initialData, departments }) => {
    const isEditMode = !!initialData;

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        employeeId: '',
        role: 'USER',
        department: '',
        authMethod: 'local',
        password: '',
        managerId: '',
        isActive: true,
        salary: 0,
        joiningDate: '',
        exitDate: '',
        employmentType: 'FULL_TIME',
        phoneNumber: '',
        personalEmail: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
        address: '',
        visaInfo: '',
        bio: '',
        skills: ''
    });

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || '',
                email: initialData.email || '',
                employeeId: initialData.employeeId || '',
                role: initialData.role || 'USER',
                department: initialData.department || '',
                authMethod: initialData.googleId ? 'google' : 'local',
                password: '',
                managerId: initialData.managerId || '',
                isActive: initialData.isActive !== false,
                salary: initialData.salary || 0,
                joiningDate: initialData.joiningDate ? initialData.joiningDate.split('T')[0] : '',
                exitDate: initialData.exitDate ? initialData.exitDate.split('T')[0] : '',
                employmentType: initialData.employmentType || 'FULL_TIME',
                phoneNumber: initialData.phoneNumber || '',
                personalEmail: initialData.personalEmail || '',
                emergencyContactName: initialData.emergencyContactName || '',
                emergencyContactPhone: initialData.emergencyContactPhone || '',
                address: initialData.address || '',
                visaInfo: initialData.visaInfo || '',
                bio: initialData.bio || '',
                skills: initialData.skills || ''
            });
        } else {
            api.get('/admin/suggest-employee-id')
                .then(response => {
                    setFormData({
                        name: '', email: '', employeeId: response.data.suggestedEmployeeId || '',
                        role: 'USER', department: '', authMethod: 'local', password: '', managerId: '', isActive: true,
                        salary: 0, joiningDate: '', exitDate: '', employmentType: 'FULL_TIME'
                    });
                })
                .catch(error => {
                    console.error('Error fetching suggested employee ID:', error);
                    setFormData({
                        name: '', email: '', employeeId: '', role: 'USER', department: '', authMethod: 'local',
                        password: '', managerId: '', isActive: true,
                        salary: 0, joiningDate: '', exitDate: '', employmentType: 'FULL_TIME',
                        phoneNumber: '', personalEmail: '', emergencyContactName: '', emergencyContactPhone: '',
                        address: '', visaInfo: '', bio: '', skills: ''
                    });
                });
        }
    }, [initialData, show]);

    if (!show) return null;

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({ ...formData, [e.target.name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData, isEditMode);
    };

    const inputStyle = {
        width: '100%',
        padding: '0.75rem',
        borderRadius: '0.5rem',
        border: '1px solid #ddd',
        fontSize: '1rem',
        marginTop: '0.25rem'
    };

    const labelStyle = {
        display: 'block',
        fontSize: '0.875rem',
        fontWeight: '500',
        color: '#374151',
        marginBottom: '0.25rem'
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white', borderRadius: '1rem', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                width: '100%', maxWidth: '28rem', padding: '1.5rem', maxHeight: '90vh', overflowY: 'auto'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', color: '#1f2937' }}>
                        {isEditMode ? <Edit2 size={20} style={{ marginRight: '0.5rem', color: '#2563eb' }} /> : <Plus size={20} style={{ marginRight: '0.5rem', color: '#2563eb' }} />}
                        {isEditMode ? 'Edit User' : 'Add New User'}
                    </h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {isEditMode && (
                            <div style={{ padding: '0.75rem', backgroundColor: formData.isActive ? '#f0fdf4' : '#fef2f2', borderRadius: '0.5rem', border: `1px solid ${formData.isActive ? '#bbf7d0' : '#fecaca'}` }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem', color: formData.isActive ? '#166534' : '#991b1b', fontWeight: '500' }}>
                                    <input
                                        type="checkbox"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                        style={{ width: '1.25rem', height: '1.25rem' }}
                                    />
                                    User Account is {formData.isActive ? 'Active' : 'Disabled'}
                                </label>
                            </div>
                        )}

                        <div>
                            <label htmlFor="user-name" style={labelStyle}>Full Name</label>
                            <input
                                id="user-name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                style={inputStyle}
                                required
                            />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div>
                                <label htmlFor="user-employee-id" style={labelStyle}>Employee ID</label>
                                <input
                                    id="user-employee-id"
                                    name="employeeId"
                                    value={formData.employeeId}
                                    onChange={handleChange}
                                    style={inputStyle}
                                    placeholder="EMP001"
                                />
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Leave empty for auto-assignment</p>
                            </div>
                            <div>
                                <label htmlFor="user-email" style={labelStyle}>Email Address</label>
                                <input
                                    id="user-email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    style={inputStyle}
                                    required
                                    disabled={isEditMode}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div>
                                <label htmlFor="user-role" style={labelStyle}>Role</label>
                                <select
                                    id="user-role"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    style={inputStyle}
                                >
                                    <option value="USER">User</option>
                                    <option value="MANAGER">Manager</option>
                                    <option value="ADMIN">Admin</option>
                                    <option value="HR">HR</option>
                                    <option value="FINANCE">Finance</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="user-department" style={labelStyle}>Primary Department</label>
                                <select
                                    id="user-department"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleChange}
                                    style={inputStyle}
                                >
                                    <option value="">Select Primary Department</option>
                                    {departments.map(dept => (
                                        <option key={dept} value={dept}>{dept}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="user-manager" style={labelStyle}>Reports To</label>
                            <select
                                id="user-manager"
                                name="managerId"
                                value={formData.managerId}
                                onChange={handleChange}
                                style={inputStyle}
                            >
                                <option value="">No Manager (Top Level)</option>
                                {managers.map(mgr => (
                                    <option key={mgr.id} value={mgr.id}>
                                        {mgr.name} ({mgr.department || 'General'})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
                            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#0f172a', marginBottom: '0.75rem' }}>Payroll Details</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label htmlFor="user-salary" style={labelStyle}>Monthly CTC</label>
                                    <input
                                        id="user-salary"
                                        name="salary"
                                        type="number"
                                        value={formData.salary || ''}
                                        onChange={handleChange}
                                        style={inputStyle}
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="user-type" style={labelStyle}>Employment Type</label>
                                    <select
                                        id="user-type"
                                        name="employmentType"
                                        value={formData.employmentType || 'FULL_TIME'}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    >
                                        <option value="FULL_TIME">Full Time</option>
                                        <option value="CONTRACTOR">Contractor</option>
                                        <option value="PARTNER">Partner</option>
                                        <option value="INTERN">Intern</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="user-join" style={labelStyle}>Joining Date</label>
                                    <input
                                        id="user-join"
                                        name="joiningDate"
                                        type="date"
                                        value={formData.joiningDate}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="user-exit" style={labelStyle}>Exit Date (Optional)</label>
                                    <input
                                        id="user-exit"
                                        name="exitDate"
                                        type="date"
                                        value={formData.exitDate}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
                            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#0f172a', marginBottom: '0.75rem' }}>HR / Private Info</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label htmlFor="user-phone" style={labelStyle}>Phone Number</label>
                                    <input id="user-phone" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label htmlFor="user-personal-email" style={labelStyle}>Personal Email</label>
                                    <input id="user-personal-email" name="personalEmail" type="email" value={formData.personalEmail} onChange={handleChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label htmlFor="user-emergency-name" style={labelStyle}>Emergency Contact Name</label>
                                    <input id="user-emergency-name" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label htmlFor="user-emergency-phone" style={labelStyle}>Emergency Contact Phone</label>
                                    <input id="user-emergency-phone" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange} style={inputStyle} />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label htmlFor="user-address" style={labelStyle}>Address</label>
                                    <textarea id="user-address" name="address" value={formData.address} onChange={handleChange} style={inputStyle} rows="2" />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label htmlFor="user-visa" style={labelStyle}>Visa / Work Permit Info</label>
                                    <input id="user-visa" name="visaInfo" value={formData.visaInfo} onChange={handleChange} style={inputStyle} />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label htmlFor="user-skills" style={labelStyle}>Skills (Comma separated)</label>
                                    <input id="user-skills" name="skills" value={formData.skills} onChange={handleChange} style={inputStyle} placeholder="JavaScript, React, Node.js" />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label htmlFor="user-bio" style={labelStyle}>Bio / Summary</label>
                                    <textarea id="user-bio" name="bio" value={formData.bio} onChange={handleChange} style={inputStyle} rows="2" />
                                </div>
                            </div>
                        </div>

                        {!isEditMode && (
                            <div>
                                <label style={{ ...labelStyle, marginBottom: '0.5rem' }}>Authentication Method</label>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <label style={{
                                        flex: 1, padding: '0.75rem', border: `1px solid ${formData.authMethod === 'local' ? '#3b82f6' : '#e5e7eb'}`,
                                        borderRadius: '0.5rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        backgroundColor: formData.authMethod === 'local' ? '#eff6ff' : 'white'
                                    }}>
                                        <input
                                            type="radio"
                                            name="authMethod"
                                            value="local"
                                            checked={formData.authMethod === 'local'}
                                            onChange={handleChange}
                                            style={{ display: 'none' }}
                                        />
                                        <Lock size={20} style={{ marginBottom: '0.25rem', color: '#4b5563' }} />
                                        <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Password</span>
                                    </label>
                                    <label style={{
                                        flex: 1, padding: '0.75rem', border: `1px solid ${formData.authMethod === 'google' ? '#3b82f6' : '#e5e7eb'}`,
                                        borderRadius: '0.5rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        backgroundColor: formData.authMethod === 'google' ? '#eff6ff' : 'white'
                                    }}>
                                        <input
                                            type="radio"
                                            name="authMethod"
                                            value="google"
                                            checked={formData.authMethod === 'google'}
                                            onChange={handleChange}
                                            style={{ display: 'none' }}
                                        />
                                        <Shield size={20} style={{ marginBottom: '0.25rem', color: '#4b5563' }} />
                                        <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Google Auth</span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {formData.authMethod === 'local' && !isEditMode && (
                            <div>
                                <label htmlFor="user-password" style={labelStyle}>Set Password</label>
                                <input
                                    id="user-password"
                                    name="password"
                                    type="text"
                                    value={formData.password}
                                    onChange={handleChange}
                                    style={{ ...inputStyle, fontFamily: 'monospace' }}
                                    placeholder="Enter initial password"
                                    required={formData.authMethod === 'local'}
                                />
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Share this password with the user securely.</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary"
                            style={{ width: '100%', marginTop: '0.5rem' }}
                        >
                            {loading ? 'Saving...' : (isEditMode ? 'Update User' : 'Create User')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const PasswordResetModal = ({ show, onClose, onSubmit, loading, userEmail }) => {
    const [newPassword, setNewPassword] = useState('');

    if (!show) return null;

    return (
        <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1001
        }}>
            <div style={{
                backgroundColor: 'white', borderRadius: '1rem', width: '100%', maxWidth: '24rem', padding: '1.5rem'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <Key size={20} style={{ marginRight: '0.5rem', color: '#f59e0b' }} />
                        Reset Password
                    </h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                        <X size={24} />
                    </button>
                </div>
                <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>Resetting password for <strong>{userEmail}</strong></p>
                <form onSubmit={(e) => { e.preventDefault(); onSubmit(newPassword); }}>
                    <input
                        type="password"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #ddd', marginBottom: '1.5rem' }}
                    />
                    <button type="submit" disabled={loading} className="btn btn-primary" style={{ width: '100%', backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}>
                        {loading ? 'Resetting...' : 'Confirm Reset'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const UserManagement = () => {
    const navigate = useNavigate();
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [resetModalUser, setResetModalUser] = useState(null);

    useEffect(() => {
        fetchDepartments();
        fetchUsers();
    }, []);

    const fetchDepartments = async () => {
        try {
            const res = await api.get('/departments');
            setDepartments(res.data.map(d => d.name));
        } catch (error) {
            console.error('Failed to fetch departments:', error);
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            setMessage({ type: 'error', text: 'Failed to load users' });
        } finally {
            setLoading(false);
        }
    };

    const potentialManagers = useMemo(() => {
        return users.filter(u => u.role === 'ADMIN' || u.role === 'MANAGER' || u.role === 'HR');
    }, [users]);

    const orgHierarchy = useMemo(() => {
        const userMap = {};
        const roots = [];
        users.forEach(u => {
            userMap[u.id] = { ...u, children: [] };
        });
        users.forEach(u => {
            if (u.managerId && userMap[u.managerId]) {
                userMap[u.managerId].children.push(userMap[u.id]);
            } else {
                roots.push(userMap[u.id]);
            }
        });
        return roots;
    }, [users]);

    const handleCreateOrUpdateUser = async (userData, isEdit) => {
        setSubmitLoading(true);
        setMessage(null);
        try {
            if (isEdit) {
                await api.put(`/admin/users/${selectedUser.id}`, userData);
                setMessage({ type: 'success', text: 'User updated successfully!' });
            } else {
                await api.post('/admin/users', userData);
                setMessage({ type: 'success', text: 'User created successfully!' });
            }
            setShowModal(false);
            fetchUsers();
            setSelectedUser(null);
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save user' });
        } finally {
            setSubmitLoading(false);
        }
    };

    const openCreateModal = () => {
        setSelectedUser(null);
        setShowModal(true);
    };

    const openEditModal = (user) => {
        const userToEdit = { ...user, managerId: user.manager?.id || '' };
        setSelectedUser(userToEdit);
        setShowModal(true);
    };

    const toggleUserStatus = async (user) => {
        if (!window.confirm(`Are you sure you want to ${user.isActive ? 'disable' : 'activate'} this user?`)) return;
        try {
            await api.put(`/admin/users/${user.id}`, { isActive: !user.isActive });
            setMessage({ type: 'success', text: `User ${user.isActive ? 'disabled' : 'activated'} successfully!` });
            fetchUsers();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to update status' });
        }
    };

    const handleResetPassword = async (newPassword) => {
        setSubmitLoading(true);
        try {
            await api.post('/auth/admin/reset-password', {
                userId: resetModalUser.id,
                newPassword: newPassword
            });
            setMessage({ type: 'success', text: `Successfully reset password for ${resetModalUser.email}` });
            setResetModalUser(null);
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to reset password' });
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className="container section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{ display: 'flex', alignItems: 'center', color: '#4b5563', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                    <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
                    Back to Home
                </button>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ background: '#f3f4f6', padding: '4px', borderRadius: '8px', display: 'flex' }}>
                        <button
                            onClick={() => setViewMode('list')}
                            style={{
                                padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '500', fontSize: '0.875rem',
                                background: viewMode === 'list' ? 'white' : 'transparent',
                                boxShadow: viewMode === 'list' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                color: viewMode === 'list' ? '#1f2937' : '#6b7280'
                            }}
                        >
                            List View
                        </button>
                        <button
                            onClick={() => setViewMode('tree')}
                            style={{
                                padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '500', fontSize: '0.875rem',
                                background: viewMode === 'tree' ? 'white' : 'transparent',
                                boxShadow: viewMode === 'tree' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                color: viewMode === 'tree' ? '#1f2937' : '#6b7280'
                            }}
                        >
                            Tree View
                        </button>
                    </div>
                    <button onClick={openCreateModal} className="btn btn-primary">
                        <Plus size={20} style={{ marginRight: '0.5rem' }} /> Add User
                    </button>
                </div>
            </div>

            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Users style={{ marginRight: '0.75rem', color: '#2563eb' }} /> HRMS
            </h1>

            {message && (
                <div style={{
                    padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem', display: 'flex', alignItems: 'center',
                    backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
                    color: message.type === 'success' ? '#166534' : '#991b1b'
                }}>
                    {message.type === 'success' ? <CheckCircle size={20} style={{ marginRight: '0.5rem' }} /> : <AlertCircle size={20} style={{ marginRight: '0.5rem' }} />}
                    {message.text}
                </div>
            )}

            {viewMode === 'list' ? (
                <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#4b5563' }}>Name</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#4b5563' }}>Employee ID</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#4b5563' }}>Email</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#4b5563' }}>Role</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#4b5563' }}>Primary Department</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#4b5563' }}>Reports To</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#4b5563' }}>Status</th>
                                    <th style={{ padding: '1rem', fontWeight: '600', color: '#4b5563', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading users...</td></tr>
                                ) : users.length === 0 ? (
                                    <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No users found.</td></tr>
                                ) : (
                                    users.map((user) => (
                                        <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6', opacity: user.isActive ? 1 : 0.6 }}>
                                            <td style={{ padding: '1rem', fontWeight: '500', color: '#111827' }}>
                                                {user.name}
                                                {!user.isActive && <span style={{ marginLeft: '0.5rem', fontSize: '0.7em', color: '#ef4444', border: '1px solid #ef4444', padding: '0 4px', borderRadius: '4px' }}>DISABLED</span>}
                                            </td>
                                            <td style={{ padding: '1rem', color: '#4b5563', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                {user.employeeId || '-'}
                                            </td>
                                            <td style={{ padding: '1rem', color: '#4b5563' }}>{user.email}</td>
                                            <td style={{ padding: '1rem' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600',
                                                    backgroundColor: user.role === 'ADMIN' ? '#f3e8ff' : user.role === 'MANAGER' ? '#dbeafe' : user.role === 'HR' ? '#fce7f3' : '#dcfce7',
                                                    color: user.role === 'ADMIN' ? '#6b21a8' : user.role === 'MANAGER' ? '#1e40af' : user.role === 'HR' ? '#be185d' : '#166534'
                                                }}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem', color: '#4b5563' }}>
                                                {user.department || '-'}
                                            </td>
                                            <td style={{ padding: '1rem', color: '#4b5563' }}>
                                                {user.manager ? (
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        <Briefcase size={14} style={{ marginRight: '0.25rem' }} /> {user.manager.name}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '1rem' }}>
                                                {user.isActive ? (
                                                    <span style={{ color: '#166534', fontWeight: '500', fontSize: '0.875rem' }}>Active</span>
                                                ) : (
                                                    <span style={{ color: '#991b1b', fontWeight: '500', fontSize: '0.875rem' }}>Disabled</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '1rem', textAlign: 'right' }}>
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                                    <button
                                                        onClick={() => openEditModal(user)}
                                                        style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #e5e7eb', background: 'white', color: '#4b5563', cursor: 'pointer' }}
                                                        title="Edit User"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => toggleUserStatus(user)}
                                                        style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #e5e7eb', background: 'white', color: user.isActive ? '#ef4444' : '#166534', cursor: 'pointer' }}
                                                        title={user.isActive ? "Disable User" : "Activate User"}
                                                    >
                                                        <Power size={16} />
                                                    </button>
                                                    {!user.googleId && !user.microsoftId && (
                                                        <button
                                                            onClick={() => setResetModalUser(user)}
                                                            style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #e5e7eb', background: 'white', color: '#f59e0b', cursor: 'pointer' }}
                                                            title="Reset Password"
                                                        >
                                                            <Key size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="glass-card" style={{ padding: '2rem', minHeight: '400px' }}>
                    <div style={{ paddingBottom: '1rem', borderBottom: '1px solid #eee', marginBottom: '1rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Organization Hierarchy</h3>
                        <p style={{ color: '#666' }}>Visualize reporting lines across the company.</p>
                    </div>
                    {orgHierarchy.length === 0 ? (
                        <p>No hierarchy data available.</p>
                    ) : (
                        <OrgTree users={orgHierarchy} />
                    )}
                </div>
            )}

            {showModal && (
                <UserModal
                    show={showModal}
                    onClose={() => setShowModal(false)}
                    onSubmit={handleCreateOrUpdateUser}
                    loading={submitLoading}
                    managers={potentialManagers}
                    initialData={selectedUser}
                    departments={departments}
                />
            )}

            {resetModalUser && (
                <PasswordResetModal
                    show={!!resetModalUser}
                    onClose={() => setResetModalUser(null)}
                    userEmail={resetModalUser.email}
                    onSubmit={handleResetPassword}
                    loading={submitLoading}
                />
            )}
        </div>
    );
};

export default UserManagement;
